import { ApiResponse, endpoint, err, getAxios, postAxios, processError, res } from "./axios";

export const API_BASE = import.meta.env.VITE_API_BASE;

interface GetAllValidationsProps {
  model?: string | null
  provider?: string | null
  geography?: string | null
  version?: string | null
  privateOnly?: boolean
  organisationId?: number | string | null
}

export const getAllValidations = async ({
  model,
  provider,
  geography,
  version,
  privateOnly = false,
  organisationId = null,
}: GetAllValidationsProps):Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/validations/${provider}/${model}/${geography}/${version}/${privateOnly}/${organisationId}`, false)

    const response = result?.data?.validations || null
    return res(response)
  } catch {
    return err("Error getting validations.")
  }
}

interface GetSignedUrlProps {
  key?: string | null
  id?: number[] | string[] | null
}

export const getSignedUrl = async ({
  key,
  id,
}: GetSignedUrlProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/validations/sign/`, {
      key,
      id,
    }, false)

    const response = result?.data?.url || null
    return res(response)
  } catch {
    return err("Error getting signed URL.")
  }
}

interface DeleteValidationProps {
  organisation_id: number | string | null
  validation_id: number | string | null
}

export const deleteValidation = async ({
  ...props
}: DeleteValidationProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/validations/delete-validation`, {...props}, false)

    const response = result?.data?.validations || null
    return res(response)
  } catch {
    return err("Error deleting validation.")
  }
}

interface DeleteValidationItemProps {
  id: number | string
  organisationId: number | string
}

export const deleteValidationItem = async ({
  ...props
}: DeleteValidationItemProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/validations/delete-item`, {...props}, false)

    const response = result?.data?.validations || null
    return res(response)
  } catch {
    return err("Error deleting validation item.")
  }
}

interface UpdateValidationProps {
  file: {
    file: File
    type: string
    content?: string
  }[]
  model: string | null
  provider: string | null
  geography: string | null
  version: string | null
  lat: number | string
  lon: number | string
  name: string | null
  organisation_id: number | string
}

export const updateValidation = async ({
  ...props
}: UpdateValidationProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/validations/update`, {...props}, false)

    const response = result?.data?.validations || null
    return res(response)
  } catch {
    return err("Error updating validation.")
  }
}

interface UpdateValidationProps {
  id: number | string | null
  existing_files: {
    content: null | string
    file: string
    type: string
    validations_content_id: number | string
  }[],
  new_files: {
    file: File
    type: string
    content?: string
  }[]
  model: string | null
  provider: string | null
  geography: string | null
  version: string | null
  lat: number | string
  lon: number | string
  name: string | null
  organisation_id: number | string
}

export const createOrUpdateValidationApi = async ({
  id,
  lat,
  lon,
  name,
  model,
  provider,
  geography,
  version,
  existing_files,
  new_files,
  organisation_id,
}: UpdateValidationProps): Promise<ApiResponse> => {
  try {
    const formData = new FormData();

    existing_files.forEach((item, index) => {
      formData.append(`existing_files[${index}]`, item.file);
      formData.append(`existing_files_types[${index}]`, item.type);
      formData.append(`existing_files_id[${index}]`, `${item.validations_content_id}`);
      if (item.content !== undefined && item.content !== null) {
        formData.append(`existing_files_contents[${index}]`, item.content);
      }
    });

    new_files.forEach((item, index) => {
      formData.append(`new_files[${index}]`, item.file);
      formData.append(`new_files_types[${index}]`, item.type);
      if (item.content !== undefined && item.content !== null) {
        formData.append(`new_files_contents[${index}]`, item.content);
      }
    });

    if (id !== undefined) formData.append('id', String(id));
    if (lat !== undefined) formData.append('lat', String(lat));
    if (lon !== undefined) formData.append('lon', String(lon));
    if (name) formData.append('name', name);
    if (organisation_id) formData.append('organisation_id', String(organisation_id));
    if (model) formData.append('model', model);
    if (provider) formData.append('provider', provider);
    if (geography) formData.append('geography', geography);
    if (version !== undefined && version !== null) {
      formData.append('version', version);
    }

    const result = await endpoint.post(
      `${API_BASE}/api/v1/validations/create-or-update`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    const response = result?.data?.result || null;
    return res(response);
  } catch (e) {
    const { status, message } = processError(
      e,
      "Error uploading data sources.",
      true
    );
    return err(message, status);
  }
};

interface LoadValidationProps {
  id: number | string
}

export const loadValidation = async ({
  id
}: LoadValidationProps):Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/validations/get-by-id/${id}`, false)

    const response = result?.data?.validations || null
    return res(response)
  } catch {
    return err("Error getting validations.")
  }
}